export type HealthStatus = 'ok' | 'degraded' | 'down';

export interface HealthCheck {
  name: string;
  status: HealthStatus;
  message?: string;
  durationMs?: number;
}

export interface HealthReport {
  global: HealthStatus;
  date: string;
  checks: HealthCheck[];
}

/**
 * Service de santé applicative : vérifie persistance, licence, version, intégrité.
 * Exposé via la vue Centre Qualité ou un endpoint /health.
 */
export class HealthCheckService {
  private checks: { name: string; fn: () => Promise<HealthCheck> }[] = [];

  register(name: string, fn: () => Promise<Omit<HealthCheck, 'name'>>): this {
    this.checks.push({
      name,
      fn: async () => {
        const start = performance.now();
        try {
          const r = await fn();
          return { name, durationMs: Math.round(performance.now() - start), ...r };
        } catch (err) {
          return {
            name,
            status: 'down',
            message: String(err),
            durationMs: Math.round(performance.now() - start),
          };
        }
      },
    });
    return this;
  }

  async run(): Promise<HealthReport> {
    const results = await Promise.all(this.checks.map((c) => c.fn()));
    const global: HealthStatus = results.some((r) => r.status === 'down')
      ? 'down'
      : results.some((r) => r.status === 'degraded')
        ? 'degraded'
        : 'ok';
    return { global, date: new Date().toISOString(), checks: results };
  }
}
